import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { completeLogin, jwtSubject, hashQuery } from './auth/session';
import './Login.css';

/**
 * Landing route for the OIDC redirect. The backend hands us a SINGLE-USE CODE at
 * #/sso/callback?code=... (never the token). We POST it to /api/sso/exchange, receive the app JWT
 * in the response body, then run the same post-login routing as password sign-in. Failures bounce
 * to /login?sso_error=...
 */
export default function SsoCallback() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;          // StrictMode double-invoke guard (code is single-use!)
    ran.current = true;

    const q = hashQuery();
    const ssoError = q.get('sso_error') || q.get('error');
    const code = q.get('code');
    const legacyToken = q.get('token');   // backward-compat; remove once backend only sends code
    if (ssoError || (!code && !legacyToken)) {
      navigate(`/login?sso_error=${encodeURIComponent(ssoError || 'callback')}`, { replace: true });
      return;
    }

    (async () => {
      try {
        let token = legacyToken;
        if (code) {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/sso/exchange`,
            { code },
            { headers: { 'Content-Type': 'application/json' } },
          );
          token = data.token;
        }
        const userName = jwtSubject(token);
        if (!token || !userName) {
          navigate('/login?sso_error=token', { replace: true });
          return;
        }
        await completeLogin({ token, userName, navigate, i18n });
      } catch {
        setError(t('auth.ssoExchangeError', 'We could not complete sign-in.'));
        navigate('/login?sso_error=exchange', { replace: true });
      }
    })();
  }, [navigate, i18n, t]);

  return (
    <div className="login-page">
      <div className="login-card">
        <h2>{t('auth.ssoCompleting', 'Completing sign-in…')}</h2>
        {error
          ? <p className="error-message">{error}</p>
          : <p style={{ fontSize: '0.9rem', color: '#555' }}>{t('auth.ssoPleaseWait', 'Please wait while we sign you in.')}</p>}
      </div>
    </div>
  );
}
